import { nextTick, onBeforeUnmount, onMounted, onUpdated, ref, watch } from 'vue';

const SCROLL_THRESHOLD = 100;

export function useStreamingScroll(messages) {
  const content = ref(null);
  const showScrollButton = ref(false);

  // AI reply index expected after the next submission
  let pendingReplyIndex = 0;
  let pendingScrollToTop = false;
  let followToBottom = false;
  // user manually scrolled up while streaming => pause autoscroll follow
  let userScrolled = false;
  // detect upward user scroll
  let lastScrollTop = 0;
  let pendingScrollEvents = 0;
  let wasTyping = false;

  const checkScrollPosition = () => {
    if (!content.value) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = content.value;
    showScrollButton.value = scrollHeight - scrollTop - clientHeight - 20 > SCROLL_THRESHOLD;
  };

  const setScrollTop = (target) => {
    if (!content.value) {
      return;
    }

    const max = content.value.scrollHeight - content.value.clientHeight;
    const clamped = Math.min(max, Math.max(0, target));
    if (Math.round(clamped) !== Math.round(content.value.scrollTop)) {
      pendingScrollEvents++;
    }

    lastScrollTop = clamped;
    content.value.scrollTop = clamped;
  };

  const scrollToBottom = () => {
    if (!content.value) {
      return;
    }

    followToBottom = true;
    userScrolled = false;
    setScrollTop(content.value.scrollHeight - content.value.clientHeight);
  };

  const scrollMessageToTop = (index) => {
    if (!content.value) {
      return;
    }

    const el = content.value.children[index];
    if (!el) {
      return;
    }

    const containerRect = content.value.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    setScrollTop(content.value.scrollTop + (elRect.top - containerRect.top));
  };

  // resets scroll state so scrolling starts fresh.
  const prepareForNewMessage = () => {
    pendingReplyIndex = messages.value.length + 1;
    pendingScrollToTop = true;
    followToBottom = false;
    userScrolled = false;
    showScrollButton.value = false;
  };

  const resetScrollState = () => {
    showScrollButton.value = false;
    followToBottom = false;
    userScrolled = false;
  };

  const onContentScroll = () => {
    if (!content.value) {
      return;
    }

    const { scrollTop } = content.value;

    if (pendingScrollEvents > 0) {
      pendingScrollEvents--;
      lastScrollTop = scrollTop;
      checkScrollPosition();
      return;
    }

    if (scrollTop < lastScrollTop - 5) {
      userScrolled = true;
      followToBottom = false;
      checkScrollPosition();
    }
    lastScrollTop = scrollTop;
  };

  onUpdated(() => {
    const isTyping = messages.value.some((m) => m.isTyping);

    if (!userScrolled && content.value) {
      if (followToBottom) {
        setScrollTop(content.value.scrollHeight - content.value.clientHeight);
      } else if (isTyping) {
        const typingIndex = messages.value.findIndex((m) => m.isTyping);
        const aiEl = typingIndex >= 0 ? content.value.children[typingIndex] : null;

        if (aiEl) {
          const maxScroll = content.value.scrollHeight - content.value.clientHeight;
          const aiTopRelativeToContainer = aiEl.offsetTop - content.value.scrollTop;

          // Keep scrolling to the bottom (which pushes the AI element upward)
          // until its top reaches the container's top edge, then stop.
          if (aiTopRelativeToContainer > 0) {
            setScrollTop(maxScroll);
          }
        }
      } else if (wasTyping) {
        // typing just ended — one final scroll to reveal addidtional content
        setScrollTop(content.value.scrollHeight - content.value.clientHeight);
      }
    }

    if (!isTyping && !followToBottom) {
      userScrolled = false;
    }

    wasTyping = isTyping;
    checkScrollPosition();
  });

  const attachListener = (el) => el.addEventListener('scroll', onContentScroll, { passive: true });
  const detachListener = (el) => el.removeEventListener('scroll', onContentScroll);

  onMounted(() => {
    if (content.value) {
      attachListener(content.value);
    }
  });

  onBeforeUnmount(() => {
    if (content.value) {
      detachListener(content.value);
    }
  });

  watch(content, (el, prevEl) => {
    if (prevEl) {
      detachListener(prevEl);
    }

    if (el) {
      attachListener(el);
      nextTick(() => {
        setScrollTop(el.scrollHeight - el.clientHeight);
        checkScrollPosition();
      });
    }
  });

  // scroll to top of user message when AI reply first appears
  watch(
    () => messages.value.length,
    (newLength, oldLength) => {
      if (newLength <= oldLength) {
        return;
      }

      nextTick(() => {
        if (pendingScrollToTop && newLength > pendingReplyIndex) {
          requestAnimationFrame(() => scrollMessageToTop(pendingReplyIndex - 1));
          pendingScrollToTop = false;
        } else if (!pendingScrollToTop) {
          if (content.value) {
            setScrollTop(content.value.scrollHeight - content.value.clientHeight);
          }
          checkScrollPosition();
        }
      });
    },
    { deep: true },
  );

  return {
    content,
    showScrollButton,
    scrollToBottom,
    scrollMessageToTop,
    checkScrollPosition,
    prepareForNewMessage,
    resetScrollState,
  };
}
